import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { CultureIndexEntry } from "../../services/api/types/Members";
import { ChartContainer } from "../ChartTile/ChartTile.styles";

import { getDot } from "./colorScheme";
import { CultureIndex, EUScore } from "./CultureIndexTable.styles";

const defaultCult: CultureIndexEntry = {
  associateId: "",
  chartName: "",
  classification: "",
  date: "",
  euVal: 0,
  pointColor: "",
  pointLetter: "",
  pointNum: 0,
  pointRgb1: 0,
  pointRgb2: 0,
  pointRgb3: 0,
  redArrowValue: 0,
};

function makeColor(culture: CultureIndexEntry) {
  return `rgb(${255 * culture.pointRgb1},${255 * culture.pointRgb2},${
    255 * culture.pointRgb3
  })`;
}

const CustomizedDot: React.FC<{
  cx?: number;
  cy?: number;
  payload?: CultureIndexEntry;
  lineless?: boolean;
}> = ({ cx = 0, cy = 0, payload = defaultCult, lineless = false }) => {
  //offset to keep in the bottom chart
  if (payload.pointLetter === "L" && lineless) {
    cy += 8;
  } else if (payload.pointLetter === "I" && lineless) {
    cy -= 7;
  }
  return (
    <svg x={cx - 10} y={cy - 10}>
      {getDot(makeColor(payload))}
    </svg>
  );
};

enum ChartNames {
  behaviors = "Job Behaviors",
  traits = "Survey Traits",
}

export const CultureIndexTable: React.FC<{
  data: Array<CultureIndexEntry>;
  name: string;
}> = ({ data, name }) => {
  data = data
    .filter(entry => entry.chartName === ChartNames.traits)
    .sort((a, b) => (a.pointLetter < b.pointLetter ? -1 : 1));
  return (
    <ChartContainer>
      <LineChart
        layout="vertical"
        width={490}
        height={220}
        data={data.slice(0, 4)}
        margin={{
          // top: 20,
          right: 10,
          left: 0,
          bottom: 5,
        }}
      >
        <CartesianGrid strokeDasharray="0" vertical={false} stroke="#E8E8E8" />
        <XAxis
          strokeWidth={0}
          interval={0}
          tickCount={11}
          orientation="top"
          type="number"
          domain={[0, 10]}
          tickLine={false}
          tickMargin={7}
          padding={{ left: 3, right: 6 }}
          tick={{
            fontFamily: "Open Sans",
            fontSize: "12px",
            fontStyle: "normal",
            fontWeight: 600,
            letterSpacing: "0.2px",
            color: "#737B7D",
          }}
        />
        <XAxis
          strokeWidth={0}
          xAxisId="bottom"
          interval={0}
          tickCount={11}
          orientation="bottom"
          type="number"
          domain={[0, 10]}
          tickLine={false}
          tickMargin={20}
          padding={{ left: 3, right: 6 }}
          tick={{
            fontFamily: "Open Sans",
            fontSize: "12px",
            fontStyle: "normal",
            fontWeight: 600,
            letterSpacing: "0.2px",
            color: "#737B7D",
          }}
        />
        {!!data.length && (
          <YAxis
            dataKey="pointLetter"
            type="category"
            tickLine={false}
            axisLine={false}
            tickMargin={5}
            tick={{
              fontFamily: "Open Sans",
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: 600,
              letterSpacing: "0.2px",
              color: "#737B7D",
            }}
          />
        )}
        {!!data.length && (
          <ReferenceLine
            x={data[0].redArrowValue}
            stroke="#FF6B6B"
            viewBox={"0 0 1 1"}
          />
        )}
        <Line
          dataKey="pointNum"
          type="monotone"
          stroke="#737B7D"
          strokeWidth={1.25}
          dot={<CustomizedDot lineless={false} />}
        />
      </LineChart>

      <EUScore>{data.length ? `EU = ${data[0].euVal}` : ""}</EUScore>

      <LineChart
        layout="vertical"
        width={490}
        height={98}
        data={data.slice(4).reverse()}
        margin={{
          top: 25,
          right: 10,
          left: 0,
          bottom: 10,
        }}
      >
        <CartesianGrid
          strokeDasharray="0"
          vertical={false}
          stroke="#F4F5F4"
          fill="#F4F5F4"
        />
        <XAxis
          strokeWidth={0}
          interval={0}
          tickCount={11}
          orientation="top"
          type="number"
          domain={[0, 10]}
          tickLine={false}
          tickMargin={7}
          padding={{ left: 3, right: 6 }}
          tick={{
            fontFamily: "Open Sans",
            fontSize: "12px",
            fontStyle: "normal",
            fontWeight: 600,
            letterSpacing: "0.2px",
            color: "#737B7D",
          }}
          hide={true}
        />
        <XAxis
          strokeWidth={0}
          tickLine={false}
          xAxisId="bottom"
          interval={0}
          tickCount={11}
          orientation="bottom"
          type="number"
          domain={[0, 10]}
          tickMargin={20}
          padding={{ left: 3, right: 6 }}
          tick={{
            fontFamily: "Open Sans",
            fontSize: "12px",
            fontStyle: "normal",
            fontWeight: 600,
            letterSpacing: "0.2px",
            color: "#737B7D",
          }}
          hide={true}
        />
        {!!data.length && (
          <YAxis
            dataKey="pointLetter"
            type="category"
            tickLine={false}
            axisLine={false}
            tickMargin={5}
            tick={{
              fontFamily: "Open Sans",
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: 600,
              letterSpacing: "0.2px",
              color: "#737B7D",
            }}
          />
        )}
        {!!data.length && (
          <Line
            dataKey="pointNum"
            type="monotone"
            stroke="none"
            strokeWidth={1.25}
            dot={<CustomizedDot lineless={true} />}
          />
        )}
      </LineChart>

      {data.length ? (
        <CultureIndex>
          {name} is a {data[0].classification}
        </CultureIndex>
      ) : (
        <CultureIndex>No Culture Index Data</CultureIndex>
      )}
    </ChartContainer>
  );
};
